/**
 * errText — safe axios-error → string coercion.
 *
 * The FastAPI backend sometimes returns 403 / 400 with a DICT-shaped
 * `detail` payload rather than a bare string. The MFA enforcer is the
 * canonical example:
 *
 *   HTTP/1.1 403 Forbidden
 *   { "detail": { "code": "MFA_ENFORCEMENT_REQUIRED",
 *                 "message": "Two-factor authentication is mandatory…",
 *                 "must_enable_by": "2026-09-07T…" } }
 *
 * If the caller does `setErr(e?.response?.data?.detail)` and later renders
 * `{err}` in JSX, React 18 crashes with error #31 ("objects are not valid
 * as a React child") and the entire route unmounts — the page goes to
 * the top-level error boundary.
 *
 * This helper always returns a **string**, so downstream renderers can
 * blindly interpolate it:
 *
 *     const [err, setErr] = useState('');
 *     try { … }
 *     catch (e) { setErr(errText(e, 'Save failed')); }
 *     ...
 *     {err && <div>{err}</div>}
 *
 * Object detail → prefers `detail.message`, then `detail.detail`, then a
 * JSON stringify as last-resort forensic output. Pydantic 422 arrays are
 * flattened to `field: msg; field: msg`.
 */
export default function errText(e, fallback = 'Request failed') {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    // Pydantic validation error array
    return detail
      .map((d) => `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg}`)
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.detail || JSON.stringify(detail);
  }
  return e?.message || fallback;
}
